import type {
  CampaignStatus,
  CreateTaskParamsItem,
} from '@/api/activityWhatsApp/data.d';
import type { CommonOptionsType } from '@/definition/common/data.d';
import type { BuiltInSegmentItemType } from '@/pages/segmentsManage/utils/typings/addOrEdit';
/** 步骤 */
export enum StepEnum {
  /** 发件人 */
  SENDER = 'sender',
  /** 收件人 */
  RECIPIENTS = 'recipients',
  /** 内容设置 */
  CONTENT = 'content',
  /** 递送计划 */
  SCHEDULE = 'schedule',
}

/** 发件人表单数据 */
export interface SenderFormData {
  /** 发件人名称 */
  senderName: string;
  /** WhatsApp 业务账号 */
  businessAccount: string;
  /** WhatsApp 手机号 */
  phoneNumber: string;
}

/** 步骤组件通用Props */
export interface StepPropsType {
  /** 页面操作类型 */
  action: string;
  /** 下一步 */
  onNext: (data: any) => void;
  onChange: (data: any) => void;
  /** 静默提交 */
  onSilentSubmit: (data: any) => void;
  /** 是否展示步骤操作按钮 */
  isStepOperation: boolean;
  /** 改变 */
  onValuesChange: (data: any) => void;
  goToPages?: (url: string, params?: any) => void;
  /** 详情数据 */
  detailData: FormDataType;
  formCommonProps: any;
  isDetails: boolean;
  isAdd: boolean;
  isCopy: boolean;
  setRuleDetailsLoading: (loading: boolean) => void;
  setSaveList: (list: StepEnum[]) => void;
  setDisabledList: (list: StepEnum[]) => void;
  setIsPageLoading: (isPageLoading: boolean) => void;
  onExpandStep?: (stepId: StepEnum) => void;
}

export interface FormDataType {
  id?: number;
  /** 活动名称 */
  name: string;
  /** 发件账号ID */
  whatsAppAccountId?: number;
  /** 发件账号名称 */
  whatsAppAccountName?: string;
  /** 发件手机号 */
  senderPhoneNumber: string;
  /** 1：标签，2：细分规则 3: 全部联系人 */
  segmentType: ToTabsEnum;
  /** 标签 */
  segmentId?: number;
  segmentName?: string;
  segmentTo?: string;
  /** 描述 */
  description?: string;
  /** 游戏 */
  gameId?: string;
  /** 默认语言 */
  defaultLanguage: string;
  /** 内容模板 */
  contentTemplate: CreateTaskParamsItem[];
  /** 递送类型 1：按固定时区 ，2：按用户所在时区；3：按地点配置 */
  deliverType: number;
  /** 递送时间 */
  deliverTime?: string;
  /** 订阅人数 */
  totalUserCount?: number;
  /** 状态 */
  status: CampaignStatus;
  // 开始时间
  startTime?: string;
  /** 目标联系人 */
  senderTo?: string;
  /** 内置分层条件列表 */
  builtInSegmentList?: BuiltInSegmentItemType[] | null;
  /** 内置分层uuid */
  builtInSegmentUuid?: string | null;
}

/** 提交表单 - 类型 */
export enum SubmitTypeEnum {
  /** 默认 */
  default = 'default',
  /** 草稿 */
  draft = 'draft',
  /** 跳转保存草稿 */
  jump_save_draft = 'jumpDraft',
}

/** 按钮 - 操作枚举 */
export enum OperationEnum {
  /** 默认提交 */
  DEFAULT_SUBMIT = 'DEFAULT_SUBMIT',
  /** 保存草稿 */
  SAVE_DRAFT = 'SAVE_DRAFT',
  /** 预览测试 */
  PREVIEW_TEST = 'PREVIEW_TEST',
}

export enum ToTabsEnum {
  /** 默认 */
  default = 0,
  /** 标签 */
  label = 1,
  /** 分层 */
  Segment = 2,
  /** 所有联系人 */
  AllContact = 3,
  /** 内置分层 */
  builtinSegment = 4,
}

/** 递送类型 */
export enum DeliverTypeEnum {
  /** 按固定时区 */
  fixed = 1,
  /** 按用户所在时区 */
  userLocation = 2,
  /** 按地点配置 */
  country = 3,
}

export enum ApprovalStatusEnum {
  /** 待审批 */
  PENDING = 'PENDING',
  /** 已通过 */
  APPROVED = 'APPROVED',
  /** 已驳回 */
  REJECTED = 'REJECTED',
  /** 禁用 */
  DISABLED = 'DISABLED',
}

/** 审批状态 */
export const approvalStatusOptions: CommonOptionsType[] = [
  {
    label: '待审批',
    labelKey: 'ACTIVITY.MANAGE.TO.BE.APPROVED',
    theme: 'success',
    value: ApprovalStatusEnum.PENDING,
  },
  {
    label: '已通过',
    labelKey: 'ACTIVITY.MANAGE.PASSED',
    theme: 'primary',
    value: ApprovalStatusEnum.APPROVED,
  },
  {
    label: '已驳回',
    labelKey: 'ACTIVITY.MANAGE.REJECTED',
    theme: 'danger',
    value: ApprovalStatusEnum.REJECTED,
  },
  {
    label: '已禁用',
    labelKey: 'ACTIVITY.MANAGE.DISABLED',
    theme: 'danger',
    value: ApprovalStatusEnum.DISABLED,
  },
];

// 语言对象
type DefaultLangType = {
  value: string;
  name: string;
  label: string;
  proportion: number;
  isDefault: boolean; // 是否是常驻tab语言
  isActive?: boolean; // 是否已经完成填写
  templateName?: string; // 模板名称
  templateId?: string; // 模板ID
  key: string; // 唯一标识
};

// 计算语言占比
type DefaultLangOptionsType = {
  defaultLang: DefaultLangType;
  hasProportionList: DefaultLangType[];
  defaultLangList: DefaultLangType[];
};

type ToStatisticsType = {
  total: number;
  defaultLangOptions: DefaultLangOptionsType;
};

// 规则详情数据
export interface RuleDetailsData {
  subscriptionCount: number; // 订阅人数
  taskPredictDuration: number; // 任务预测持续时间
  langData?: Record<string, number>; // 语言数据
  regionData?: Record<string, number>; // 地区数据
  /** whatsapp订阅人数 */
  whatsappSubscriptionCount: number;
  /** whatsapp语言统计 */
  whatsappLangData: Record<string, number>;
  /** whatsapp国家统计 */
  whatsappRegionData: Record<string, number>;
}
